import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Clock,
  Play,
  Pause,
  Square,
  Settings,
  Target,
  TrendingUp,
  Brain,
  AlertCircle,
  CheckCircle,
  Zap,
  Moon,
  Sun,
  Coffee,
  Volume2,
  VolumeX,
  Flame,
  Timer,
  Sparkles,
  BarChart3,
  History,
  Sliders,
  Lightbulb,
  ShieldOff,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService, type FocusSession } from '@/services/tabs';
import { enableFocusBlocking, disableFocusBlocking } from '@/services/extensionBridge';
import {
  startAmbientSound,
  stopAmbientSound,
  previewAmbientSound,
  playSessionChime,
  requestNotificationPermission,
  showSessionNotification,
  AMBIENT_SOUND_OPTIONS,
  type AmbientSoundType,
} from '@/utils/ambientSound';
import { BlockRulesPanel } from '@/components/productivity/BlockRulesPanel';

interface FocusSettings {
  sessionDuration: number;
  breakDuration: number;
  longBreakDuration: number;
  sessionsUntilLongBreak: number;
  ambientSound: boolean;
  soundType: string;
  blockWebsites: boolean;
  blockedSites: string[];
  notifications: boolean;
}

/**
 * Inline toggle row used inside the settings panel. Defined at module
 * level so the same component instance is reused across renders
 * instead of being recreated on every FocusModePage render.
 */
const ToggleRow: React.FC<{
  label: string;
  icon?: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ label, icon, checked, onChange }) => (
  <label className="flex items-center justify-between cursor-pointer gap-3">
    <span className="text-sm font-medium text-khiimori-text-secondary flex items-center gap-2 min-w-0">
      {icon}
      {label}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${
        checked
          ? 'bg-violet-500 border-violet-400'
          : 'bg-khiimori-bg-elevated/30 border-khiimori-border/30'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform duration-200 ease-out ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </label>
);

/**
 * Daily-rotating focus tips. Module-level constant so we never call
 * hooks in the middle of the component body. The same user sees the
 * same tip all day; a fresh one tomorrow.
 */
const FOCUS_TIPS: readonly string[] = [
  'Start with the smallest concrete step. A two-minute starter dissolves the activation cost far better than planning the whole hour.',
  'Single-task. Switching between two tasks costs the same as a 40% productivity drop. Close every tab that is not the one you are on.',
  'Make the next action physical. "Write the introduction" is a wish. "Open the doc and type one sentence" is a command the brain obeys.',
  'Defend a start time, not a duration. Telling yourself "I will start at 14:07" is dramatically more reliable than "I will focus for an hour sometime today".',
  'Phone face-down, in another room. Glance = 23 minutes to recover from. The drawer is the cheapest focus tool you own.',
  'Drink water before you start. Mild dehydration measurably slows attention. A glass of water is a free 10% focus boost.',
  'Write the one thing you will finish, on a sticky note, on the screen. When the timer asks, you always know the answer.',
  'Stand up for the first 60 seconds. Movement tells the brain this is not a fake work block — it primes the alert systems you actually need.',
  'Pre-decide the stop condition. "I will stop when the email is sent" is a clean exit. "I will stop when I feel done" never fires.',
  'Take the break. The work you do in the second half of a session is only as good as the rest you took before it.',
];

const FocusModePage: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [sessionType, setSessionType] = useState<'focus' | 'break' | 'longBreak'>('focus');
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [currentGoal, setCurrentGoal] = useState('');
  const [distractionCount, setDistractionCount] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [settings, setSettings] = useState<FocusSettings>(() => {
    const DEFAULTS: FocusSettings = {
      sessionDuration: 25,
      breakDuration: 5,
      longBreakDuration: 15,
      sessionsUntilLongBreak: 4,
      ambientSound: false,
      soundType: 'rain',
      blockWebsites: true,
      blockedSites: ['facebook.com', 'twitter.com', 'reddit.com'],
      notifications: true,
    };
    try {
      const saved = localStorage.getItem('khiimori-focus-settings');
      if (!saved) return DEFAULTS;
      const parsed = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return DEFAULTS;
      }
      // Merge with strict per-field validation so any non-boolean /
      // non-string / non-array value in storage falls back to a safe
      // default rather than crashing a downstream `.map()` / `.slice()`.
      const p = parsed as Record<string, unknown>;
      return {
        sessionDuration: typeof p.sessionDuration === 'number' ? p.sessionDuration : DEFAULTS.sessionDuration,
        breakDuration: typeof p.breakDuration === 'number' ? p.breakDuration : DEFAULTS.breakDuration,
        longBreakDuration: typeof p.longBreakDuration === 'number' ? p.longBreakDuration : DEFAULTS.longBreakDuration,
        sessionsUntilLongBreak: typeof p.sessionsUntilLongBreak === 'number' ? p.sessionsUntilLongBreak : DEFAULTS.sessionsUntilLongBreak,
        ambientSound: typeof p.ambientSound === 'boolean' ? p.ambientSound : DEFAULTS.ambientSound,
        soundType: typeof p.soundType === 'string' ? p.soundType : DEFAULTS.soundType,
        blockWebsites: typeof p.blockWebsites === 'boolean' ? p.blockWebsites : DEFAULTS.blockWebsites,
        blockedSites: Array.isArray(p.blockedSites)
          ? (p.blockedSites as unknown[]).filter((s): s is string => typeof s === 'string')
          : DEFAULTS.blockedSites,
        notifications: typeof p.notifications === 'boolean' ? p.notifications : DEFAULTS.notifications,
      };
    } catch {
      return DEFAULTS;
    }
  });

  const [recentSessions, setRecentSessions] = useState<FocusSession[]>([]);
  const [todayStats, setTodayStats] = useState({
    totalFocusTime: 0,
    sessionsCompleted: 0,
    averageDistractions: 0,
    longestStreak: 0,
    productivityScore: 0
  });

  const [blockedSitesFromRules, setBlockedSitesFromRules] = useState<string[]>([]);

  // Guard against corrupted localStorage shapes: if a previous version
  // of the app (or a manual edit) stored `blockedSites` as anything
  // other than a string array, fall back to the default list instead
  // of crashing the whole hub with a TypeError on `.map()`.
  const effectiveBlockedSites: string[] =
    Array.isArray(blockedSitesFromRules) && blockedSitesFromRules.length > 0
      ? blockedSitesFromRules
      : Array.isArray(settings.blockedSites)
        ? settings.blockedSites
        : ['facebook.com', 'twitter.com', 'reddit.com'];

  useEffect(() => {
    try {
      localStorage.setItem('khiimori-focus-settings', JSON.stringify(settings));
    } catch {
      // ignore quota errors
    }
  }, [settings]);

  useEffect(() => {
    if (blockedSitesFromRules.length > 0 && !settings.blockWebsites) {
      setSettings((prev) => ({ ...prev, blockWebsites: true }));
    }
  }, [blockedSitesFromRules.length, settings.blockWebsites]);

  const refreshFocusData = async () => {
    try {
      const [status, sessions, stats] = await Promise.all([
        tabService.getFocusModeStatus(),
        tabService.getFocusSessions(),
        tabService.getFocusStats(),
      ]);
      setIsActive(status.enabled ?? false);
      if (status.duration) {
        setTimeLeft(status.duration * 60);
        setSettings((prev) => ({ ...prev, sessionDuration: status.duration ?? prev.sessionDuration }));
      }
      setRecentSessions(sessions);
      setTodayStats(stats);
      setSessionsCompleted(stats.sessionsCompleted);
    } catch {
      // Backend unavailable — continue with local state
    }
  };

  // Sync focus state with backend on mount
  useEffect(() => {
    refreshFocusData();
  }, []);

  const applyBlocking = useCallback(
    (active: boolean) => {
      if (!settings.blockWebsites || effectiveBlockedSites.length === 0) return;
      if (active && sessionType === 'focus') {
        enableFocusBlocking(effectiveBlockedSites, settings.sessionDuration);
      } else {
        disableFocusBlocking();
      }
    },
    [settings.blockWebsites, effectiveBlockedSites, settings.sessionDuration, sessionType],
  );

  // Ambient sound during active focus sessions
  useEffect(() => {
    const shouldPlay = isActive && !isPaused && settings.ambientSound && sessionType === 'focus';
    if (shouldPlay) {
      void startAmbientSound(settings.soundType as AmbientSoundType);
    } else {
      void stopAmbientSound();
    }
    return () => {
      void stopAmbientSound();
    };
  }, [isActive, isPaused, settings.ambientSound, settings.soundType, sessionType]);

  // Extension blocking during focus (not breaks)
  useEffect(() => {
    if (isActive && !isPaused && sessionType === 'focus') {
      applyBlocking(true);
    } else if (!isActive) {
      disableFocusBlocking();
    } else {
      disableFocusBlocking();
    }
    return () => {
      if (!isActive) disableFocusBlocking();
    };
  }, [isActive, isPaused, sessionType, applyBlocking]);

  // Timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && !isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleSessionComplete();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, isPaused, timeLeft]);

  const handleSessionComplete = () => {
    setIsActive(false);
    disableFocusBlocking();
    void stopAmbientSound();

    if (sessionType === 'focus') {
      tabService.disableFocusMode(true).then(() => refreshFocusData()).catch(() => {});
      const newSessionsCompleted = sessionsCompleted + 1;
      setSessionsCompleted(newSessionsCompleted);

      if (settings.notifications) {
        playSessionChime('complete');
        showSessionNotification('Focus session complete!', 'Great work — time for a break.');
      }

      // Determine next session type
      if (newSessionsCompleted % settings.sessionsUntilLongBreak === 0) {
        setSessionType('longBreak');
        setTimeLeft(settings.longBreakDuration * 60);
      } else {
        setSessionType('break');
        setTimeLeft(settings.breakDuration * 60);
      }
    } else {
      if (settings.notifications) {
        playSessionChime('break');
        showSessionNotification('Break over', 'Ready for your next focus session?');
      }
      setSessionType('focus');
      setTimeLeft(settings.sessionDuration * 60);
    }
  };

  const startSession = async () => {
    if (!isActive) {
      if (settings.notifications) {
        await requestNotificationPermission();
      }
      setIsActive(true);
      setIsPaused(false);
      setTimeLeft(settings.sessionDuration * 60);
      tabService.enableFocusMode(settings.sessionDuration * 60, currentGoal || undefined).catch(() => {});
      if (settings.blockWebsites) {
        enableFocusBlocking(effectiveBlockedSites, settings.sessionDuration);
      }
      if (settings.ambientSound) {
        void startAmbientSound(settings.soundType as AmbientSoundType);
      }
    }
  };

  const pauseSession = () => {
    setIsPaused(!isPaused);
  };

  const stopSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeLeft(settings.sessionDuration * 60);
    setSessionType('focus');
    disableFocusBlocking();
    void stopAmbientSound();
    tabService.disableFocusMode(false).then(() => refreshFocusData()).catch(() => {});
  };

  const skipSession = () => {
    setTimeLeft(0);
  };

  const reportDistraction = () => {
    setDistractionCount(prev => prev + 1);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSessionIcon = () => {
    switch (sessionType) {
      case 'focus':
        return <Brain className="w-6 h-6" />;
      case 'break':
        return <Coffee className="w-6 h-6" />;
      case 'longBreak':
        return <Moon className="w-6 h-6" />;
    }
  };

  const getSessionLabel = () => {
    switch (sessionType) {
      case 'focus':
        return 'Focus Session';
      case 'break':
        return 'Short Break';
      case 'longBreak':
        return 'Long Break';
    }
  };

  const calculateProgress = () => {
    const totalTime = sessionType === 'focus'
      ? settings.sessionDuration * 60
      : sessionType === 'break'
      ? settings.breakDuration * 60
      : settings.longBreakDuration * 60;

    return ((totalTime - timeLeft) / totalTime) * 100;
  };

  // Daily-rotating focus tip. Picks one based on the day-of-year so the
  // same user sees the same tip all day but a fresh one tomorrow.
  // Pulls from a curated list of evidence-backed productivity nudges.
  const dailyFocusTip = FOCUS_TIPS[Math.floor(Date.now() / 86_400_000) % FOCUS_TIPS.length];

  // Small inline toggle row used inside the settings panel.
  // Uses a real "track + thumb" pattern (not a checkbox) so it always
  // renders as a proper switch, with the thumb positioned via `left`
  // for reliable cross-platform behavior.
  // NOTE: ToggleRow is defined at module level (below) so the same
  // component instance is reused across renders instead of being
  // recreated on every FocusModePage render.

  return (
    <PageLayout title="Focus & Timer" description="Eliminate distractions, track time, and stay in flow" icon={Target}>
      <div className="max-w-7xl mx-auto space-y-6">

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Hero: timer card on the left */}
          <div className="space-y-6 lg:col-span-2">
            {/* Timer Card */}
            <div className="relative overflow-hidden rounded-2xl border border-khiimori-border/10 bg-gradient-to-br from-khiimori-bg-elevated/10 to-khiimori-bg-elevated/0 p-8 backdrop-blur-xl">
              {/* Ambient glow when a focus session is running */}
              {isActive && !isPaused && sessionType === 'focus' && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary-500/20 blur-3xl"
                />
              )}

              <div className="relative">
                {/* Session Mode Selector Tabs (Focus / Pomodoro / Breaks) */}
                <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
                  <button
                    type="button"
                    disabled={isActive}
                    onClick={() => {
                      setSessionType('focus');
                      setTimeLeft(settings.sessionDuration * 60);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                      sessionType === 'focus'
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25 border border-primary-400/40'
                        : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 border border-khiimori-border/10'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Brain className="w-4 h-4" />
                    Focus Session
                  </button>
                  <button
                    type="button"
                    disabled={isActive}
                    onClick={() => {
                      setSessionType('break');
                      setTimeLeft(settings.breakDuration * 60);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                      sessionType === 'break'
                        ? 'bg-success-500 text-white shadow-lg shadow-success-500/25 border border-success-400/40'
                        : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 border border-khiimori-border/10'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Coffee className="w-4 h-4" />
                    Short Break ({settings.breakDuration}m)
                  </button>
                  <button
                    type="button"
                    disabled={isActive}
                    onClick={() => {
                      setSessionType('longBreak');
                      setTimeLeft(settings.longBreakDuration * 60);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 ${
                      sessionType === 'longBreak'
                        ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25 border border-purple-400/40'
                        : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 border border-khiimori-border/10'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Moon className="w-4 h-4" />
                    Long Break ({settings.longBreakDuration}m)
                  </button>
                </div>

                {/* Timer Display */}
                <div className="flex justify-center mb-6">
                  <div className="relative inline-block">
                    <svg className="w-64 h-64 sm:w-72 sm:h-72 transform -rotate-90">
                      <circle
                        cx="144"
                        cy="144"
                        r="132"
                        stroke="rgba(255,255,255,0.08)"
                        strokeWidth="10"
                        fill="none"
                      />
                      <circle
                        cx="144"
                        cy="144"
                        r="132"
                        stroke={sessionType === 'focus' ? '#8b5cf6' : '#22c55e'}
                        strokeWidth="10"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 132}`}
                        strokeDashoffset={`${2 * Math.PI * 132 * (1 - calculateProgress() / 100)}`}
                        strokeLinecap="round"
                        className="transition-all duration-1000"
                      />
                    </svg>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl sm:text-7xl font-bold tabular-nums text-khiimori-text-primary tracking-tight">
                          {formatTime(timeLeft)}
                        </div>
                        <div className="text-xs text-khiimori-text-muted mt-1">
                          {isActive ? (isPaused ? 'Paused' : 'In Progress') : 'Ready to Start'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Goal Input */}
                {sessionType === 'focus' && !isActive && (
                  <div className="mb-6 max-w-md mx-auto">
                    <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
                      Session Goal
                    </label>
                    <input
                      type="text"
                      value={currentGoal}
                      onChange={(e) => setCurrentGoal(e.target.value)}
                      placeholder="What will you focus on?"
                      className="w-full px-4 py-2.5 border border-khiimori-border/20 rounded-xl bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Control Buttons */}
                <div className="flex items-center justify-center gap-3">
                  {!isActive ? (
                    <button
                      onClick={startSession}
                      className="px-8 py-3.5 bg-primary-500 text-white rounded-xl hover:bg-primary-600 flex items-center gap-2 text-lg font-semibold shadow-lg shadow-primary-500/20 hover:shadow-xl hover:shadow-primary-500/30 transition-all"
                    >
                      <Play className="w-5 h-5" />
                      Start Focus
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={pauseSession}
                        className="px-5 py-2.5 bg-warning-500 text-khiimori-text-primary rounded-xl hover:bg-warning-600 flex items-center gap-2 font-semibold shadow-lg"
                      >
                        {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        {isPaused ? 'Resume' : 'Pause'}
                      </button>
                      <button
                        onClick={stopSession}
                        className="px-5 py-2.5 bg-error-500 text-khiimori-text-primary rounded-xl hover:bg-error-600 flex items-center gap-2 font-semibold shadow-lg"
                      >
                        <Square className="w-4 h-4" />
                        Stop
                      </button>
                      <button
                        onClick={skipSession}
                        className="px-5 py-2.5 bg-khiimori-bg-elevated/20 text-khiimori-text-primary rounded-xl hover:bg-khiimori-bg-elevated/30 flex items-center gap-2 font-semibold"
                      >
                        Skip
                      </button>
                    </>
                  )}
                </div>

                {/* Distraction Tracker — only while a focus session is running */}
                {isActive && sessionType === 'focus' && (
                  <div className="mt-6 pt-6 border-t border-khiimori-border/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-khiimori-text-secondary">
                        <AlertCircle className="w-4 h-4 text-warning-400" />
                        <span className="text-sm font-medium">
                          Distractions this session: {distractionCount}
                        </span>
                      </div>
                      <button
                        onClick={reportDistraction}
                        className="px-3 py-1.5 bg-warning-500/20 text-warning-400 border border-warning-500/30 rounded-lg hover:bg-warning-500/30 text-xs font-semibold transition-colors"
                      >
                        Report Distraction
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar: stats card + cycle tracker */}
          <div className="space-y-6">
            {/* Today's Progress — compact, visual */}
            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5 backdrop-blur-xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary-400" />
                  Today's Progress
                </h3>
                <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[10px] font-medium text-khiimori-text-muted">
                  {new Date().toLocaleDateString([], { weekday: 'short' })}
                </span>
              </div>

              <div className="space-y-4">
                {/* Focus time — primary progress */}
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-khiimori-text-muted">Focus time</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold tabular-nums text-khiimori-text-primary">
                        {todayStats.totalFocusTime}
                      </span>
                      <span className="text-xs text-khiimori-text-muted">/ 240 min</span>
                    </div>
                  </div>
                  <div className="mt-2 h-2 w-full rounded-full bg-khiimori-bg-elevated/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-violet-400 transition-all"
                      style={{ width: `${Math.min(100, (todayStats.totalFocusTime / 240) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Sessions + streak + score — small stats grid */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-khiimori-bg-elevated/5 p-2.5 text-center">
                    <div className="text-lg font-bold tabular-nums text-primary-400">
                      {todayStats.sessionsCompleted}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">
                      Sessions
                    </div>
                  </div>
                  <div className="rounded-lg bg-khiimori-bg-elevated/5 p-2.5 text-center">
                    <div className="text-lg font-bold tabular-nums text-success-400">
                      {todayStats.longestStreak}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">
                      Streak
                    </div>
                  </div>
                  <div className="rounded-lg bg-khiimori-bg-elevated/5 p-2.5 text-center">
                    <div className="text-lg font-bold tabular-nums text-warning-400">
                      {todayStats.productivityScore}
                      <span className="text-[10px] text-khiimori-text-muted">%</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">
                      Score
                    </div>
                  </div>
                </div>

                {/* Productivity score + distractions as compact bars */}
                <div className="space-y-2 pt-1">
                  <div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-khiimori-text-muted">Avg distractions</span>
                      <span className="font-semibold tabular-nums text-khiimori-text-secondary">
                        {todayStats.averageDistractions}/session
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-khiimori-bg-elevated/10">
                      <div
                        className="h-full rounded-full bg-warning-400/70"
                        style={{ width: `${Math.min(100, todayStats.averageDistractions * 20)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Start — duration presets + Start button + Settings */}
            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5 backdrop-blur-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
                  <Timer className="w-4 h-4 text-violet-400" />
                  Quick Start
                </h3>
                <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium tabular-nums text-violet-300">
                  {settings.sessionDuration}m
                </span>
              </div>
              <div className="mb-3 grid grid-cols-4 gap-1.5">
                {[15, 25, 45, 60].map((mins) => {
                  const active = settings.sessionDuration === mins;
                  return (
                    <button
                      key={mins}
                      type="button"
                      onClick={() => {
                        setSettings((prev) => ({ ...prev, sessionDuration: mins }));
                        if (!isActive) setTimeLeft(mins * 60);
                      }}
                      className={`flex h-10 flex-col items-center justify-center rounded-lg text-center transition-all ${
                        active
                          ? 'bg-gradient-to-br from-violet-500/40 to-purple-600/30 text-violet-100 ring-1 ring-violet-400/60'
                          : 'bg-khiimori-bg-elevated/30 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/60 hover:text-khiimori-text-primary'
                      }`}
                      title={`${mins} minute focus session`}
                    >
                      <span className="text-sm font-bold tabular-nums leading-none">{mins}</span>
                      <span className="text-[9px] font-medium uppercase tracking-wider opacity-70">min</span>
                    </button>
                  );
                })}
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={startSession}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/20 transition-all hover:shadow-xl hover:shadow-violet-500/30"
                >
                  <Play className="h-4 w-4" />
                  Start Focus
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-khiimori-border/20 bg-khiimori-bg-elevated/30 px-4 py-2 text-sm font-medium text-khiimori-text-secondary transition-colors hover:bg-khiimori-bg-elevated/50 hover:text-khiimori-text-primary"
                >
                  <Sliders className="h-4 w-4" />
                  Settings
                </button>
              </div>
            </div>

            {/* Pomodoro cycle tracker */}
            <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5 backdrop-blur-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-400" />
                  Pomodoro Cycle
                </h3>
                <span className="rounded-full bg-khiimori-bg-elevated/10 px-2 py-0.5 text-[10px] font-medium tabular-nums text-khiimori-text-muted">
                  {sessionsCompleted % settings.sessionsUntilLongBreak}/{settings.sessionsUntilLongBreak}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {[...Array(settings.sessionsUntilLongBreak)].map((_, i) => {
                  const completed = i < sessionsCompleted % settings.sessionsUntilLongBreak;
                  return (
                    <div
                      key={i}
                      className={`h-2 flex-1 rounded-full transition-colors ${
                        completed
                          ? 'bg-gradient-to-r from-orange-400 to-pink-500'
                          : 'bg-khiimori-bg-elevated/20'
                      }`}
                    />
                  );
                })}
              </div>
              <p className="mt-3 text-center text-[11px] text-khiimori-text-muted">
                {settings.sessionsUntilLongBreak - (sessionsCompleted % settings.sessionsUntilLongBreak)} more
                session
                {settings.sessionsUntilLongBreak - (sessionsCompleted % settings.sessionsUntilLongBreak) !== 1 ? 's' : ''}{' '}
                until long break
              </p>
            </div>
          </div>
        </div>

        {/* Recent Sessions — full width below the main grid */}
        <div className="rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-5 backdrop-blur-xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
              <History className="w-4 h-4 text-primary-400" />
              Recent Sessions
            </h3>
            <span className="text-xs text-khiimori-text-muted">
              {recentSessions.length} total
            </span>
          </div>

          {recentSessions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-khiimori-border/20 bg-khiimori-bg-elevated/5 py-8 text-center">
              <div className="rounded-full bg-khiimori-bg-elevated/10 p-2">
                <Sparkles className="h-5 w-5 text-khiimori-text-muted" />
              </div>
              <p className="text-sm font-medium text-khiimori-text-secondary">
                No sessions yet
              </p>
              <p className="max-w-xs text-xs text-khiimori-text-muted">
                Hit Start Focus above to begin your first session — they'll show up here.
              </p>
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {recentSessions.slice(0, 8).map((session) => {
                const mins = session.duration ?? 0
                return (
                  <div
                    key={session.id}
                    className="shrink-0 w-56 rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-3 hover:border-violet-500/30 hover:bg-khiimori-bg-elevated/10 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div
                        className={`flex h-7 w-7 items-center justify-center rounded-md ${
                          session.completed
                            ? 'bg-success-500/20 text-success-400'
                            : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted'
                        }`}
                      >
                        {session.completed ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          <Clock className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <span className="text-[10px] text-khiimori-text-muted">
                        {new Date(session.startTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-khiimori-text-primary truncate">
                      {session.goal || 'Focus session'}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-khiimori-text-muted">
                      <span className="rounded bg-khiimori-bg-elevated/10 px-1.5 py-0.5 tabular-nums">
                        {mins}m
                      </span>
                      {session.distractions > 0 && (
                        <span className="rounded bg-warning-500/10 px-1.5 py-0.5 text-warning-400 tabular-nums">
                          {session.distractions} dist.
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Settings overlay — two-pane layout so the screen is never empty.
          Left pane: quick-start summary (focus state, duration, goal, start
          button, today's snapshot). Right pane: actual settings (durations,
          toggles, block rules). Closes on backdrop click or ×. */}
      {settingsOpen && createPortal(
        <div
          className="modal-backdrop fixed inset-0 z-[200] flex items-stretch justify-stretch p-3 sm:p-6"
          onClick={() => setSettingsOpen(false)}
        >
          <div
            className="relative mx-auto flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-khiimori-border/20 bg-khiimori-bg-secondary shadow-2xl lg:h-auto lg:max-h-[88vh] lg:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button — floats above the split */}
            <button
              onClick={() => setSettingsOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20"
              aria-label="Close settings"
            >
              <span className="text-xl leading-none">×</span>
            </button>

            {/* LEFT — quick start + current state */}
            <div className="flex w-full flex-col gap-5 border-b border-khiimori-border/10 bg-gradient-to-br from-khiimori-bg-elevated/15 to-khiimori-bg-elevated/0 p-6 lg:w-1/2 lg:border-b-0 lg:border-r">
              <div>
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-violet-300">
                  <Sparkles className="h-3 w-3" />
                  Quick start
                </div>
                <h2 className="text-xl font-bold text-khiimori-text-primary">
                  Ready when you are
                </h2>
                <p className="mt-1 text-sm text-khiimori-text-muted">
                  Pick a duration, set a goal, and start your session.
                </p>
              </div>

              {/* Duration presets — bigger and more clickable here */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted">
                  Focus duration
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[15, 25, 45, 60].map((mins) => {
                    const active = settings.sessionDuration === mins;
                    return (
                      <button
                        key={mins}
                        type="button"
                        disabled={isActive}
                        onClick={() => {
                          setSettings((prev) => ({ ...prev, sessionDuration: mins }));
                          if (!isActive) setTimeLeft(mins * 60);
                        }}
                        className={`rounded-xl border px-2 py-3 text-center text-sm font-semibold transition-all ${
                          active
                            ? 'border-violet-500/50 bg-violet-500/20 text-violet-200 shadow-md shadow-violet-500/10'
                            : 'border-khiimori-border/20 bg-khiimori-bg-elevated/5 text-khiimori-text-secondary hover:border-violet-500/30 hover:bg-khiimori-bg-elevated/15'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <div className="text-lg tabular-nums">{mins}</div>
                        <div className="text-[10px] font-medium uppercase opacity-70">min</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Goal input — promoted to the left pane */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted">
                  Session goal
                </label>
                <input
                  type="text"
                  value={currentGoal}
                  onChange={(e) => setCurrentGoal(e.target.value)}
                  placeholder="What will you focus on?"
                  className="w-full rounded-xl border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-4 py-2.5 text-sm text-khiimori-text-primary placeholder-khiimori-text-muted focus:border-violet-500/40 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                />
              </div>

              {/* Big primary action */}
              <button
                type="button"
                onClick={() => {
                  setSettingsOpen(false);
                  void startSession();
                }}
                disabled={isActive}
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 px-5 py-3.5 text-base font-semibold text-white shadow-lg shadow-violet-500/20 transition-all hover:shadow-xl hover:shadow-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Play className="h-5 w-5" />
                {isActive ? 'Session in progress' : `Start ${settings.sessionDuration}m focus`}
              </button>

              {/* Today's snapshot */}
              <div className="mt-1 grid grid-cols-3 gap-2 rounded-2xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 p-3">
                <div className="text-center">
                  <div className="text-lg font-bold tabular-nums text-primary-400">
                    {todayStats.totalFocusTime}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Focus min</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold tabular-nums text-success-400">
                    {todayStats.sessionsCompleted}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Sessions</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold tabular-nums text-warning-400">
                    {todayStats.productivityScore}%
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Score</div>
                </div>
              </div>
            </div>

            {/* RIGHT — actual settings */}
            <div className="flex w-full flex-col gap-5 overflow-y-auto p-6 lg:w-1/2">
              <div>
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-khiimori-text-muted">
                  <Sliders className="h-3 w-3" />
                  Settings
                </div>
                <h2 className="text-xl font-bold text-khiimori-text-primary">Tune your session</h2>
                <p className="mt-1 text-sm text-khiimori-text-muted">
                  Durations, sounds, and the sites you want blocked.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-khiimori-text-secondary block mb-1.5">
                    Focus duration (min)
                  </label>
                  <input
                    type="number"
                    value={settings.sessionDuration}
                    onChange={(e) => {
                      const mins = parseInt(e.target.value) || 25;
                      setSettings({ ...settings, sessionDuration: mins });
                      if (!isActive) setTimeLeft(mins * 60);
                    }}
                    className="w-full rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-3 py-2 text-sm text-khiimori-text-primary"
                    min="1"
                    max="120"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-khiimori-text-secondary block mb-1.5">
                    Break duration (min)
                  </label>
                  <input
                    type="number"
                    value={settings.breakDuration}
                    onChange={(e) =>
                      setSettings({ ...settings, breakDuration: parseInt(e.target.value) })
                    }
                    className="w-full rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-3 py-2 text-sm text-khiimori-text-primary"
                    min="1"
                    max="30"
                  />
                </div>
              </div>

              <div className="space-y-3 border-t border-khiimori-border/10 pt-4">
                <ToggleRow
                  icon={
                    settings.ambientSound ? (
                      <Volume2 className="w-4 h-4" />
                    ) : (
                      <VolumeX className="w-4 h-4" />
                    )
                  }
                  label="Ambient sound"
                  checked={settings.ambientSound}
                  onChange={(enabled) => {
                    setSettings({ ...settings, ambientSound: enabled });
                    if (enabled && !isActive) {
                      void previewAmbientSound(settings.soundType as AmbientSoundType);
                    } else if (!enabled) {
                      void stopAmbientSound();
                    }
                  }}
                />

                {settings.ambientSound && (
                  <div className="flex gap-2 pl-7">
                    <select
                      value={settings.soundType}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSettings({ ...settings, soundType: next });
                        if (isActive && sessionType === 'focus' && !isPaused) {
                          void startAmbientSound(next as AmbientSoundType);
                        } else if (!isActive) {
                          void previewAmbientSound(next as AmbientSoundType);
                        }
                      }}
                      className="flex-1 rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-3 py-2 text-sm text-khiimori-text-primary"
                    >
                      {AMBIENT_SOUND_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      title="Preview sound"
                      disabled={isActive}
                      onClick={() => void previewAmbientSound(settings.soundType as AmbientSoundType)}
                      className="shrink-0 rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/10 px-3 py-2 text-khiimori-text-secondary hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <ToggleRow
                  label="Block websites"
                  checked={settings.blockWebsites}
                  onChange={(v) => setSettings({ ...settings, blockWebsites: v })}
                />

                {settings.blockWebsites && (
                  <div className="pl-1">
                    <BlockRulesPanel
                      compact
                      onActiveSitesChange={setBlockedSitesFromRules}
                    />
                  </div>
                )}

                <ToggleRow
                  label="Notifications"
                  checked={settings.notifications}
                  onChange={(v) => setSettings({ ...settings, notifications: v })}
                />
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </PageLayout>
  );
};

export default FocusModePage;
